import type {
  Ability,
  SessionHour,
  SessionPreferences,
  SessionWindow,
  WindowLabel,
} from "./types";
import { comfortVerdict, type SessionContext } from "./comfort";
import { prefAbility, prefScoringKey } from "./preferences";
import { isSupProfileKey } from "../conditions/sport-profiles";

/** Minimum platform score (0-10) for an in-range hour to count as a real session window. */
export const SCORE_THRESHOLD: Record<Ability, number> = {
  beginner: 5.0,
  intermediate: 4.5,
  advanced: 4.0,
  expert: 3.5,
};

type Category = "ok" | "marginal" | "too-strong" | "too-weak" | "drop";

type Group<THour extends SessionHour> = {
  dayOffset: 0 | 1;
  category: Category;
  hours: THour[];
  fits: number[];
};

function pad(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}

function categorise(
  prefs: SessionPreferences,
  h: SessionHour,
  ctx: SessionContext,
  threshold: number
): Category {
  if (h.safetyState === "flagged-red") return "drop";
  const hourlyUnavailable = h.safetyState === undefined || h.safetyState === "no-data";
  if (h.dayOffset === 0 && hourlyUnavailable && ctx.safetyState === "flagged-red") {
    return "drop";
  }
  const v = comfortVerdict(prefs, h, ctx);
  if (v.excludedBy === "timing" || v.excludedBy === "water temp" || v.excludedBy === "water quality") {
    return "drop";
  }
  if (v.fitAvailability === "unavailable") return "drop";
  if (!v.acceptable && v.band === "in-range") return "drop";
  if (v.band === "too-strong") return "too-strong";
  if (v.band === "too-weak") return "too-weak";
  return h.score >= threshold ? "ok" : "marginal";
}

function mean(xs: number[]): number {
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

function trendMultiplier(scores: number[]): number {
  if (scores.length < 2) return 1;
  const delta = scores[scores.length - 1] - scores[0];
  if (delta > 0.5) return 1.1;
  if (delta < -0.5) return 0.9;
  return 1;
}

function fitScoreFor<THour extends SessionHour>(
  group: Group<THour>,
  includeTrend: boolean,
): number {
  const fittedQuality = mean(group.hours.map((h, index) => h.score * group.fits[index]));
  const trend = includeTrend ? trendMultiplier(group.hours.map((h) => h.score)) : 1;
  return Math.max(0, Math.min(100, fittedQuality * 10 * trend));
}

/** Length (hours) of the tightened headline peak inside a wider window. */
const PEAK_HOURS = 2;

/**
 * Best contiguous PEAK_HOURS-long slice of a window by sum(score * fitMultiplier).
 * `fits` is the per-hour fitMultiplier, aligned to `hours`. A window <=PEAK_HOURS
 * long is its own peak. Ties resolve to the earliest slice.
 */
export function pickPeak<THour extends SessionHour>(
  hours: readonly THour[],
  fits: readonly number[]
): { startHour: number; endHour: number } {
  if (hours.length === 0) throw new Error("pickPeak: hours must be non-empty");
  const len = Math.min(PEAK_HOURS, hours.length);
  let bestI = 0;
  let bestVal = -Infinity;
  for (let i = 0; i + len <= hours.length; i++) {
    let v = 0;
    for (let k = i; k < i + len; k++) v += hours[k].score * fits[k];
    if (v > bestVal) {
      bestVal = v;
      bestI = i;
    }
  }
  return { startHour: hours[bestI].hour, endHour: hours[bestI + len - 1].hour + 1 };
}

export function buildWindows<THour extends SessionHour>(
  hours: readonly THour[],
  prefs: SessionPreferences,
  ctx: SessionContext
): SessionWindow<THour>[] {
  const ability = prefAbility(prefs);
  const threshold = SCORE_THRESHOLD[ability];
  const includeTrend = !isSupProfileKey(prefScoringKey(prefs));

  // Daylight only, sorted by day then hour.
  const day = hours
    .filter((h) => h.isDay)
    .slice()
    .sort((a, b) => a.dayOffset - b.dayOffset || a.hour - b.hour);

  // Group consecutive hours that share a category. Dropped hours break runs.
  const groups: Group<THour>[] = [];
  let current: Group<THour> | null = null;
  for (const h of day) {
    const v = comfortVerdict(prefs, h, ctx);
    const cat = categorise(prefs, h, ctx, threshold);
    if (cat === "drop") {
      current = null;
      continue;
    }
    const contiguous =
      current &&
      current.category === cat &&
      current.dayOffset === h.dayOffset &&
      current.hours[current.hours.length - 1].hour === h.hour - 1;
    if (!contiguous) {
      current = { dayOffset: h.dayOffset, category: cat, hours: [], fits: [] };
      groups.push(current);
    }
    current!.hours.push(h);
    current!.fits.push(v.fitMultiplier);
  }

  // Rank the "ok" groups to assign best / good / possible.
  const okGroups = groups
    .filter((g) => g.category === "ok")
    .map((g) => ({ g, fit: fitScoreFor(g, includeTrend) }))
    .sort((a, b) => b.fit - a.fit);
  const rankLabel = new Map<Group<THour>, WindowLabel>();
  okGroups.forEach(({ g }, i) => {
    rankLabel.set(g, i === 0 ? "best" : i === 1 ? "good" : "possible");
  });

  const windows: SessionWindow<THour>[] = groups.map((g) => {
    const startHour = g.hours[0].hour;
    const endHour = g.hours[g.hours.length - 1].hour + 1;
    const avgScore = mean(g.hours.map((h) => h.score));
    const fitScore = fitScoreFor(g, includeTrend);
    const peak = pickPeak(g.hours, g.fits);
    const peakHours = g.hours.filter((h) => h.hour >= peak.startHour && h.hour < peak.endHour);
    const peakScore = peakHours.length ? mean(peakHours.map((h) => h.score)) : avgScore;
    const label: WindowLabel =
      g.category === "ok"
        ? (rankLabel.get(g) ?? "possible")
        : g.category === "marginal"
          ? "not-recommended"
          : g.category === "too-strong"
            ? "too-strong"
            : "too-weak";

    return {
      dayOffset: g.dayOffset,
      dayLabel: g.dayOffset === 0 ? "Today" : "Tomorrow",
      startHour,
      endHour,
      timeLabel: `${pad(startHour)} - ${pad(endHour)}`,
      peakStartHour: peak.startHour,
      peakEndHour: peak.endHour,
      peakScore,
      label,
      avgScore,
      fitScore,
      hours: g.hours,
      headline: "",
      explanation: "",
      warnings: [],
    };
  });

  return windows.sort((a, b) => a.dayOffset - b.dayOffset || a.startHour - b.startHour);
}
